import { canonicalizeQuestFilter } from 'lib/questFilterCanonicalization'

// Which stream produced an exposure, and how it was ordered at that moment.
//
// This is the context that makes an impression interpretable: position 3 of a
// value-ranked Public feed and position 3 of a chronological Following feed are
// completely different observations, and neither can be reconstructed after the
// fact from the row itself. Recorded alongside every engagement event.
export interface EngagementSurface {
    surface: string
    scope?: string
    orderKey?: string
}

type SurfaceFilter = Record<string, any> | null | undefined

// Mirrors EngagementEvent::LABEL_RE server-side; a value that cannot round-trip
// is dropped rather than sent and silently rejected.
const LABEL_RE = /^[a-z][a-z0-9_]*$/i
const MAX_LABEL_LENGTH = 32

export const UNKNOWN_SURFACE = 'unknown'

function label(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined

    const trimmed = value.trim()
    if (!trimmed || trimmed.length > MAX_LABEL_LENGTH || !LABEL_RE.test(trimmed)) return undefined

    return trimmed
}

// Order matters: a filter routinely carries several truthy keys (a user's
// ordinals stream is both `user_ordinals` and `user`), so the most specific
// slice has to win.
function resolveSlice(filter: SurfaceFilter): { surface: string; scope?: string } {
    if (!filter) return { surface: UNKNOWN_SURFACE }

    if (filter.questId) return { surface: 'thread' }
    if (filter.user_ordinals) return { surface: 'ordinals', scope: 'user' }
    // The Market surface: the inventory grid (Items, which also carries the
    // ordinals key) and the card feed (Activity) are different ranked lists
    // with different position-bias curves, and neither is the ordinals wall.
    if (filter.market)
        return { surface: 'market', scope: filter.market_cards ? 'activity' : 'items' }
    if (filter.ordinals) {
        return { surface: 'ordinals', scope: label(filter.ordinals_scope) || 'public' }
    }
    if (filter.notifications) {
        const scope = filter.upvalue_notis
            ? 'upvalue'
            : filter.system_notis
            ? 'system'
            : filter.message_notis
            ? 'messages'
            : 'all'
        return { surface: 'notifications', scope }
    }
    if (filter.history) return { surface: 'history' }
    if (filter.starred) return { surface: 'starred' }
    if (filter.query || filter.search) return { surface: 'search' }
    // A meme hub's representative-thread cards and the threads inside one
    // selected meme are different ranked lists and need different position
    // curves. Personal browsing is likewise kept out of public-hub CTR.
    if (filter.meme) return { surface: 'memes', scope: 'threads' }
    if (filter.memes) {
        return { surface: 'memes', scope: label(filter.memes_scope) || 'public' }
    }
    if (filter.streams) {
        return { surface: 'streams', scope: label(filter.streams_scope) || 'public' }
    }
    // A team (stream) feed: selectTeam builds `{ team_ids: [id] }` and clears
    // every other surface key, so nothing above can match. Without this branch
    // one of the app's principal reading surfaces logs as 'unknown'.
    if (Array.isArray(filter.team_ids) && filter.team_ids.length > 0) {
        return { surface: 'stream' }
    }
    if (filter.user || filter.public_user || filter.from_user) {
        return { surface: 'user', scope: label(filter.profile_thread_scope) || 'posts' }
    }
    if (filter.clip) return { surface: 'clips', scope: 'mine' }
    if (filter.public_clip) return { surface: 'clips', scope: 'public' }
    if (filter.link) return { surface: 'shared' }
    if (filter.participants) return { surface: 'people' }
    if (filter.following) return { surface: 'home', scope: 'following' }
    if (filter.private) {
        const scope = filter.private_mentions ? 'mentions' : filter.private_all ? 'all' : 'threads'
        return { surface: 'private', scope }
    }
    if (filter.all) return { surface: 'all' }
    if (filter.hodlocker) return { surface: 'hodlocker' }
    if (filter.twetch) return { surface: 'twetch' }
    if (filter.treechat) return { surface: 'treechat' }
    if (filter.public) return { surface: 'public' }

    return { surface: UNKNOWN_SURFACE }
}

export function resolveEngagementSurface(filter: SurfaceFilter): EngagementSurface {
    const slice = resolveSlice(filter)

    // `filter.order` is frequently undefined, meaning "the viewer's default" —
    // recording that as null would lose the ordering for most Home impressions.
    // canonicalizeQuestFilter resolves the default exactly the way the request
    // does, and leaves slices that have no order concept (ordinals,
    // notifications, history) untouched.
    let orderKey: string | undefined
    try {
        const canonical = canonicalizeQuestFilter(filter) || filter
        orderKey = label(canonical?.order) || label(filter?.order)
    } catch {
        orderKey = label(filter?.order)
    }

    return { ...slice, orderKey }
}

// A broad surface label is not enough for rank attribution: position 4 in the
// Ordinals Market tab is unrelated to position 4 on its Public tab, and the same
// feed under two ranking orders has two different position-bias curves.
export function sameEngagementSurfaceContext(
    left: EngagementSurface,
    right: EngagementSurface,
): boolean {
    return (
        left.surface === right.surface &&
        (left.scope || undefined) === (right.scope || undefined) &&
        (left.orderKey || undefined) === (right.orderKey || undefined)
    )
}
